import Box, { IBoxProps } from 'components/Box';
import Text from 'components/Text';
import { IStaffPayrollPrintData } from 'features/report/services/types/reportStaff';
import { get } from 'lodash';
import { formatCurrency } from 'utils/formatCurrency';
import StaffDateTable from './StaffDateTable';
import StaffTicketTable from './StaffTicketTable';
import * as PayrollComponents from './components';
import { type IPayrollComponent, ReportPayrollCaption3Row } from './components';
import StaffHistoryTimeClocks from './StaffHistoryTimeClocks';

export type IUIStaffFieldItem<T = IStaffPayrollPrintData> = {
  label?: string | ((data?: T) => string);
  typeValue?: 'string' | 'currency';
  fieldName?: FieldPath<T> | ((data?: T) => string | number);
  hidden?: boolean | ((data?: T) => boolean);
  fields?: IUIStaffFieldItem[];
  component?: keyof typeof PayrollComponents;
  bt?: IBoxProps['bt'];
  className?: string;
  hideValue?: boolean;
};
type StaffPayrollDetailProps = {
  data?: IStaffPayrollPrintData,
  cashLabel: string;
  singleDate?: boolean;
  singleStaff?: boolean;
  fields?: IUIStaffFieldItem[];
  detailUI?: boolean;
  yearlyReport?: boolean;
  className?: string;
};
const StaffPayrollDetail = ({ fields, data, singleDate, detailUI, yearlyReport, className }: StaffPayrollDetailProps) => {
  const isHoursSalary = data?.salaryType === 'HOURS';

  const renderItem = (o: IUIStaffFieldItem<IStaffPayrollPrintData>, i: number) => {
    const Component: IPayrollComponent = o.component ? PayrollComponents[o.component] : ReportPayrollCaption3Row;
    if (typeof o.hidden === 'function' ? o.hidden(data) : o.hidden) return null;
    const dataField = typeof o.fieldName === 'function' ? o.fieldName(data) : get(data, o.fieldName || '', '');
    const label = typeof o.label === 'function' ? o.label(data) : o.label;
    return (
      <Box bt={o.bt} key={label}>
        {!!o.fieldName && <Component
          label={label}
          className={o.className}
          amount={o.hideValue ? ' ' : o.typeValue === 'string' ? dataField : formatCurrency(dataField)}
        />}
        {o.fields?.length && <>{o.fields.map(renderItem)}</>}
      </Box>
    );
  };

  if (detailUI) {
    if (isHoursSalary) return (
      <Box className={className}>
        <Box>{fields?.map(renderItem)}</Box>
        <StaffHistoryTimeClocks
          data={data?.historyTimeClocks}
          tip={data?.tipCollected}
          totalPaidOut={data?.totalPayroll}
          staffName={data?.staffName}
          regularHours={data?.regularHours}
          regularPayAmount={data?.regularPayAmount}
          overtimeHours={data?.overtimeHours}
          overtimePayAmount={data?.overtimePayAmount}
        />
      </Box>
    );
    return <Box className={className}>
      <Box>{fields?.map(renderItem)}</Box>
    </Box>;
  }

  if (isHoursSalary) return (
    <Box className={className}>
      <Box className="center" highlight>
        <Text printMode variant="CAPTION_2" className='title-highlight'>
          {data?.staffName}
        </Text>
      </Box>
      <Box>{fields?.map(renderItem)}</Box>
      <StaffHistoryTimeClocks
        data={data?.historyTimeClocks}
        tip={data?.tipCollected}
        totalPaidOut={data?.totalPayroll}
        staffName={data?.staffName}
        regularHours={data?.regularHours}
        regularPayAmount={data?.regularPayAmount}
        overtimeHours={data?.overtimeHours}
        overtimePayAmount={data?.overtimePayAmount}
        yearly={data?.salaryType === 'HOURS' && yearlyReport}
      />
    </Box>
  );

  return (
    <Box className={className}>
      <Box className="center" highlight>
        <Text printMode variant="CAPTION_2" className='title-highlight'>
          {data?.staffName}
        </Text>
      </Box>
      <Box>
        {!yearlyReport && (data?.details?.length ?? 0) > 0 && (
          singleDate
            ? <StaffTicketTable data={data?.details ?? []} />
            : <StaffDateTable data={data?.details ?? []} />
        )}

        {fields?.map(renderItem)}
      </Box>
    </Box>
  );
};

export default StaffPayrollDetail;
